import React, { useState, useEffect } from 'react';
import { 
    getNotificationPermission, 
    requestFCMToken, 
    isSafari,
    isNotificationSupported,
    saveTokenToBackend 
} from '../../cms/utils/firebaseUtils';

const NotificationBar = ({ onTokenReceived }) => {
    const [showBar, setShowBar] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);

    useEffect(() => {
        checkShouldShowBar();
    }, []);

    const checkShouldShowBar = () => {
        // check if should show notification bar
        if (!isNotificationSupported()) {
            return;
        }

        const permission = getNotificationPermission();
        const isUserSafari = isSafari();
        
        // only show in safari browser and permission is default (not decided)
        if (isUserSafari && permission === 'default' && !isDismissed) {
            // check if already dismissed (using localStorage)
            const dismissed = localStorage.getItem('notification-bar-dismissed');
            if (dismissed) {
                // check if expired
                const dismissedDate = new Date(dismissed);
                const now = new Date();
                if (now > dismissedDate) {
                    // expired, clear localStorage and show notification bar
                    localStorage.removeItem('notification-bar-dismissed');
                    setShowBar(true);
                }
            } else {
                setShowBar(true);
            }
        }
    };

    const handleEnableNotifications = async () => {
        setIsLoading(true);
        try {
            const token = await requestFCMToken();
            onTokenReceived && onTokenReceived(token);
            setShowBar(false);
            console.log("FCM Token from bar:", token);
            
            // save token to backend
            try {
                await saveTokenToBackend(token);
            } catch (error) {
                console.error('Failed to save token to backend:', error);
            }
        } catch (error) {
            console.error("Error requesting FCM token from bar:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDismiss = () => {
        setShowBar(false);
        setIsDismissed(true);
        // remember user's choice, do not show for one day
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        localStorage.setItem('notification-bar-dismissed', tomorrow.toISOString());
    };

    if (!showBar) {
        return null;
    }

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                <div style={styles.message}>
                    <span style={styles.icon}>🔔</span>
                    <span style={styles.text}>Enable Notification to Receive Important Updates</span>
                </div>
                <div style={styles.actions}>
                    <button 
                        onClick={handleEnableNotifications}
                        disabled={isLoading}
                        style={styles.enableButton}
                    >
                        {isLoading ? 'Enabling...' : 'Enable'}
                    </button>
                    <button 
                        onClick={handleDismiss}
                        style={styles.dismissButton}
                    >
                        ×
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        backgroundColor: '#007bff',
        color: '#fff',
        padding: '12px 16px',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    },
    content: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '1200px',
        margin: '0 auto'
    },
    message: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    icon: {
        fontSize: '16px'
    },
    text: {
        fontSize: '14px',
        fontWeight: '500'
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    enableButton: {
        backgroundColor: '#fff',
        color: '#007bff',
        border: 'none',
        borderRadius: '4px',
        padding: '6px 12px',
        fontSize: '12px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    dismissButton: {
        backgroundColor: 'transparent',
        color: '#fff',
        border: 'none',
        fontSize: '18px',
        cursor: 'pointer',
        padding: '2px 6px',
        borderRadius: '2px',
        lineHeight: '1'
    }
};

export default NotificationBar; 